'use client';

import { ReactNode, useEffect, useState, useCallback, useRef } from 'react';

import { useMediaQuery } from '@monoimin/hooks';

import { getArrowId } from '../../utils';

export interface ClientTimelineProps {
  uid: string;
  slidesCount: number;
  children: ReactNode[];
}

export const ClientTimeline = ({
  children,
  slidesCount,
  uid,
}: ClientTimelineProps) => {
  const [active, setActive] = useState(0);
  const [max, setMax] = useState(slidesCount as number);
  const slideToNext = () => active < max && setActive((s) => s + 1);

  const slideToPrev = useCallback(() => {
    if (active > 0) {
      setActive((s) => s - 1);
    }
  }, [active]);

  useEffect(() => {
    const { prevArrow, nextArrow } = getArrows(uid);

    if (prevArrow) {
      prevArrow.onclick = () => {
        slideToPrev();
      };
    }

    if (nextArrow) {
      nextArrow.onclick = () => {
        slideToNext();
      };
    }
  }, [slideToPrev, slideToNext]);

  useEffect(() => {
    const { prevArrow, nextArrow } = getArrows(uid);

    if (prevArrow && active <= 0) {
      prevArrow.classList.add(`opacity-30`);
    } else if (prevArrow) {
      prevArrow.classList.remove(`opacity-30`);
    }

    if (nextArrow && active === max) {
      nextArrow.classList.add(`opacity-50`);
    } else if (nextArrow) {
      nextArrow.classList.remove(`opacity-50`);
    }
  }, [active]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const isTablet = useMediaQuery(`(min-width: 640px) and (max-width: 1024px)`);

  const checkMax = useCallback(() => {
    if (isTablet) {
      setMax(slidesCount - 3);
    } else {
      setMax(slidesCount - 4);
    }
  }, [isTablet, slidesCount]);

  useEffect(() => checkMax(), [checkMax]);
  useEffect(() => {
    if (containerRef.current) {
      const container = containerRef.current;
      const scrollStep = container.scrollWidth / slidesCount;

      container.scrollTo({
        left: scrollStep * active,
        behavior: 'smooth',
      });
    }
  }, [active, containerRef, slidesCount]);
  return (
    <div
      className='snap-x snap-mandatory w-full flex overflow-x-scroll scrollbar-hide'
      ref={containerRef}
    >
      {children}
    </div>
  );
};

const getArrows = (uid: string) => {
  const prevArrowId = getArrowId(uid, 'prev');
  const nextArrowId = getArrowId(uid, 'next');

  const prevArrow = document.querySelector(`#${prevArrowId}`) as HTMLElement;
  const nextArrow = document.querySelector(`#${nextArrowId}`) as HTMLElement;

  return { prevArrow, nextArrow };
};
